"use client";

import React from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";

type TBreadCrumbProps = {
  capitalizeLinks?: boolean;
  countryCode?: string;
};

const Breadcrumbs = ({ capitalizeLinks, countryCode }: TBreadCrumbProps) => {
  const paths = usePathname();
  const pathNames = paths.split("/").filter((path) => path);

  const separator = (
    <svg
      xmlns="https://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1}
      stroke="currentColor"
      className="w-4 h-4 text-gray-600"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.25 4.5 7.5 7.5-7.5 7.5"
      />
    </svg>
  );

  // SCHEMA LTD JSON

  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "https://smss.biz";

  const breadcrumbList = pathNames.map((link, index) => {
    const href = `/${pathNames.slice(0, index + 1).join("/")}`;
    const isLast = index === pathNames.length - 1;
    const entry: Record<string, unknown> = {
      "@type": "ListItem",
      position: index + 2, // Home is position 1, path segments start at 2
      name: capitalizeLinks
        ? link.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : link,
    };
    // Google recommends omitting `item` on the last breadcrumb (current page)
    if (!isLast) {
      entry.item = `${baseUrl}${href}`;
    }
    return entry;
  });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: baseUrl,
      },
      ...breadcrumbList,
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <nav aria-label="Breadcrumb">
        <ul className="space-x-1 flex flex-wrap items-start py-5">
          <li className="listclass">
            <Link href={"/"}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-4 h-4 text-gray-400 hover:text-gray-600"
              >
                <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
                <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
              </svg>
              <span className="sr-only">Home</span>
            </Link>
          </li>
          {pathNames.length > 0 && separator}
          {pathNames.map((link, index) => {
            let href = `/${pathNames.slice(0, index + 1).join("/")}`;
            const isLastItem = index === pathNames.length - 1;
            const isCurrentPage = paths === href;

            // Check if it's the last item in the path and if it should include the country code
            let itemLink = capitalizeLinks
              ? link.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
              : link;

            if (isLastItem && countryCode) {
              itemLink = `${countryCode}${itemLink}`;
            }

            return (
              <React.Fragment key={index}>
                <li
                  className={`${
                    isCurrentPage
                      ? "text-primary font-semibold"
                      : "text-gray-400 hover:text-gray-500 "
                  } text-xs font-medium ${!isCurrentPage ? "hover:underline" : ""}`}
                >
                  {isCurrentPage ? (
                    <span>{itemLink}</span>
                  ) : (
                    <Link href={href}>{itemLink}</Link>
                  )}
                </li>
                {!isLastItem && separator}
              </React.Fragment>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default Breadcrumbs;
